import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { AnnotationCategory, FigmaAnnotationRequest, FigmaAnnotationResponse } from '../types/annotation';
import EndpointExampleSelector from './EndpointExampleSelector';

interface AnnotationFormProps {
  frameId: string;
  nodeId: string;
  nodeName?: string;
  nodeType?: string;
  pinXPercent?: number;
  pinYPercent?: number;
  editingAnnotation?: FigmaAnnotationResponse | null;
  frames: { id: string; name: string }[];
  currentFrameId: string;
  pickedNextFrameId?: string | null;
  isPickingNextFrame?: boolean;
  onPickedNextFrameConsumed?: () => void;
  onRequestNextFramePick?: () => void;
  onSave: (request: FigmaAnnotationRequest) => void;
  onCancel: () => void;
}

const CATEGORIES: AnnotationCategory[] = ['Action', 'Navigation', 'State Change', 'Validation', 'API Call', 'Note'];

const AnnotationForm: React.FC<AnnotationFormProps> = ({
  frameId,
  nodeId,
  nodeName,
  nodeType,
  pinXPercent,
  pinYPercent,
  editingAnnotation,
  frames,
  currentFrameId,
  pickedNextFrameId,
  isPickingNextFrame,
  onPickedNextFrameConsumed,
  onRequestNextFramePick,
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<AnnotationCategory>('Note');
  const [specId, setSpecId] = useState<number | undefined>();
  const [endpointPath, setEndpointPath] = useState('');
  const [endpointMethod, setEndpointMethod] = useState('');
  const [exampleName, setExampleName] = useState('');
  const [nextFrameId, setNextFrameId] = useState('');
  const [nextFrameLabel, setNextFrameLabel] = useState('');
  const [filteredFrames, setFilteredFrames] = useState<string[]>([]);

  const frameOptions = useMemo(() => {
    return frames
      .filter(frame => frame.id !== currentFrameId)
      .map(frame => ({
        id: frame.id,
        name: frame.name,
        label: `${frame.name} (${frame.id})`,
      }));
  }, [frames, currentFrameId]);

  const frameLabelById = useMemo(() => {
    const map = new Map<string, string>();
    frameOptions.forEach(option => {
      map.set(option.id, option.label);
    });
    return map;
  }, [frameOptions]);

  const frameIdByLabel = useMemo(() => {
    const map = new Map<string, string>();
    frameOptions.forEach(option => {
      map.set(option.label, option.id);
    });
    return map;
  }, [frameOptions]);

  useEffect(() => {
    if (editingAnnotation) {
      setTitle(editingAnnotation.title);
      setDescription(editingAnnotation.description || '');
      setCategory(editingAnnotation.category);
      setSpecId(editingAnnotation.specId || undefined);
      setEndpointPath(editingAnnotation.endpointPath || '');
      setEndpointMethod(editingAnnotation.endpointMethod ? editingAnnotation.endpointMethod.toLowerCase() : '');
      setExampleName(editingAnnotation.exampleName || '');
      setNextFrameId(editingAnnotation.nextFrameId || '');
      setNextFrameLabel('');
    }
  }, [editingAnnotation]);

  useEffect(() => {
    if (pickedNextFrameId) {
      setNextFrameId(pickedNextFrameId);
      setNextFrameLabel(frameLabelById.get(pickedNextFrameId) || '');
      onPickedNextFrameConsumed?.();
    }
  }, [pickedNextFrameId, frameLabelById, onPickedNextFrameConsumed]);

  useEffect(() => {
    if (!nextFrameId) return;
    const label = frameLabelById.get(nextFrameId);
    if (label && label !== nextFrameLabel) {
      setNextFrameLabel(label);
    }
  }, [nextFrameId, frameLabelById, nextFrameLabel]);

  useEffect(() => {
    setFilteredFrames(frameOptions.map(option => option.label));
  }, [frameOptions]);

  useEffect(() => {
    if (category !== 'Navigation' && nextFrameId) {
      setNextFrameId('');
      setNextFrameLabel('');
    }
  }, [category, nextFrameId]);

  const filterFrames = useCallback(
    (query: string) => {
      const lower = query.toLowerCase();
      const results = frameOptions
        .map(option => option.label)
        .filter(label => label.toLowerCase().includes(lower));
      setFilteredFrames(results);
    },
    [frameOptions]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    onSave({
      frameId,
      nodeId,
      nodeName,
      nodeType,
      title: title.trim(),
      description: description.trim() || undefined,
      category,
      pinXPercent,
      pinYPercent,
      specId,
      endpointPath: endpointPath || undefined,
      endpointMethod: endpointMethod || undefined,
      exampleName: exampleName || undefined,
      nextFrameId: category === 'Navigation' && nextFrameId ? nextFrameId : undefined,
    });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
    boxSizing: 'border-box',
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <h4 style={{ margin: '0 0 4px 0' }}>
        {editingAnnotation ? 'Edit Annotation' : 'New Annotation'}
      </h4>

      {nodeName && (
        <div style={{ fontSize: '12px', color: '#666', background: '#f0f0f0', padding: '4px 8px', borderRadius: '4px' }}>
          Node: {nodeName} ({nodeType})
        </div>
      )}

      <div>
        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Title *</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What does this component do?"
          style={inputStyle}
          required
        />
      </div>

      <div>
        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Category</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              style={{
                padding: '4px 10px',
                fontSize: '12px',
                border: category === cat ? '2px solid #4A90D9' : '1px solid #ddd',
                borderRadius: '12px',
                background: category === cat ? '#e8f0fe' : '#fff',
                cursor: 'pointer',
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Detailed behavior description..."
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      {category === 'Navigation' && (
        <div>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
            Next Frame
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <AutoComplete
                value={nextFrameLabel}
                suggestions={filteredFrames}
                completeMethod={e => filterFrames(e.query)}
                onChange={e => {
                  const value = e.value || '';
                  setNextFrameLabel(value);
                  if (!value) {
                    setNextFrameId('');
                  }
                }}
                onSelect={e => {
                  const label = e.value as string;
                  const id = frameIdByLabel.get(label);
                  if (id) {
                    setNextFrameId(id);
                    setNextFrameLabel(label);
                  }
                }}
                onBlur={() => {
                  const id = frameIdByLabel.get(nextFrameLabel);
                  if (id) {
                    setNextFrameId(id);
                  }
                }}
                dropdown
                placeholder={frameOptions.length > 0 ? 'Search frame...' : 'No frames available'}
                style={{ width: '100%' }}
                inputStyle={{ width: '100%', fontSize: '13px' }}
                itemTemplate={(label: string) => {
                  const id = frameIdByLabel.get(label);
                  const name = label.replace(/\s*\([^)]+\)$/, '');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>{name}</span>
                      {id && <span style={{ fontSize: '11px', color: '#64748b' }}>{id}</span>}
                    </div>
                  );
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => onRequestNextFramePick?.()}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                background: '#f8fafc',
                cursor: 'pointer',
                fontSize: '12px',
                whiteSpace: 'nowrap',
              }}
            >
              {isPickingNextFrame ? 'Cancel pick' : 'Pick from carousel'}
            </button>
          </div>
        </div>
      )}

      <EndpointExampleSelector
        specId={specId}
        endpointPath={endpointPath}
        endpointMethod={endpointMethod}
        exampleName={exampleName}
        onSpecChange={setSpecId}
        onEndpointChange={(path, method) => { setEndpointPath(path); setEndpointMethod(method); }}
        onExampleChange={setExampleName}
      />

      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <button
          type="submit"
          disabled={!title.trim()}
          style={{
            flex: 1,
            padding: '8px',
            background: title.trim() ? '#4A90D9' : '#ccc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: title.trim() ? 'pointer' : 'default',
            fontSize: '13px',
          }}
        >
          {editingAnnotation ? 'Update' : 'Create'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '8px 16px',
            background: '#fff',
            color: '#666',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default AnnotationForm;
